import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Loader2, CheckCircle, Phone, FileText, Footprints, ClipboardList, ArrowRight, Lock, User, ChevronDown } from 'lucide-react';
import { useConfig } from '@/context/ConfigContext';
import {
  FOOT_OPTIONS,
  COMMUNITY_PASSPORT_OPTIONS,
  resolveOtherValue,
} from '../constants/playerFields';

const BACKEND_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");
const API = `${BACKEND_URL}/api`;

// ── Custom dropdown con soporte para "Otro/Otra" + carga desde catálogo ────
function SelectField({
  name, value, onChange, options, placeholder, testId,
  otherValue, onOtherChange,
  catalogType,  // si se provee, carga opciones desde /api/catalogs/{type}
  scoutCode,    // si se provee, usa el endpoint público
}) {
  const [open, setOpen] = useState(false);
  const [catalogOptions, setCatalogOptions] = useState(null);
  const ref = useRef(null);
  const isOther = value === 'Otra' || value === 'Otro';

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!catalogType) return;
    const url = scoutCode
      ? `${API}/public/catalogs/${catalogType}?code=${scoutCode}`
      : `${API}/catalogs/${catalogType}`;
    axios.get(url)
      .then(res => setCatalogOptions(res.data || []))
      .catch(() => setCatalogOptions([]));
  }, [catalogType, scoutCode]);

  // Si tenemos catalogType, usamos esos valores + "Otra" como opción especial
  const finalOptions = catalogType
    ? [
        ...((catalogOptions || []).map(v => ({ value: v, label: v }))),
        { value: 'Otra', label: 'Otra (escribir)' },
      ]
    : options.map(o => typeof o === 'string' ? { value: o, label: o } : o);

  const selected = finalOptions.find(o => o.value === value);
  const inputBase = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/20 focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-all duration-300 outline-none font-light text-sm";

  return (
    <div data-testid={testId}>
      <div ref={ref} className="relative">
        <button
          type="button"
          onClick={() => setOpen(p => !p)}
          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-left text-sm font-light transition-all duration-300 outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20 flex items-center justify-between gap-2"
          style={{ color: selected ? '#fff' : 'rgba(255,255,255,0.2)' }}
        >
          <span className="truncate">{selected ? selected.label : (placeholder || 'Seleccionar...')}</span>
          <ChevronDown className={`w-4 h-4 flex-shrink-0 text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="absolute z-50 w-full mt-1.5 bg-[#1c1c1e] border border-white/15 rounded-xl shadow-2xl overflow-hidden max-h-56 overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange({ target: { name, value: '' } }); setOpen(false); }}
              className="w-full px-4 py-2.5 text-left text-sm text-white/30 hover:bg-white/5 transition-colors border-b border-white/5"
            >
              {placeholder || 'Seleccionar...'}
            </button>
            {finalOptions.map(o => (
              <button
                key={o.value}
                type="button"
                onClick={() => { onChange({ target: { name, value: o.value } }); setOpen(false); }}
                className={`w-full px-4 py-2.5 text-left text-sm transition-colors hover:bg-white/8 ${
                  value === o.value ? 'bg-white/10 text-white font-medium' : 'text-white/80'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {isOther && onOtherChange && (
        <input
          type="text"
          value={otherValue || ''}
          onChange={(e) => onOtherChange(name, e.target.value)}
          className={inputBase + " mt-2"}
          placeholder="Especificá cuál..."
          autoFocus
          data-testid={`${testId || name}-other-input`}
        />
      )}
    </div>
  );
}

const Header = () => {
  const config = useConfig();
  return (
    <header className="sticky top-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-center">
        <img src={config.AGENCY_LOGO_LIGHT_URL} alt={config.AGENCY_NAME} className="h-10" />
      </div>
    </header>
  );
};

export default function PlayerForm() {
  const config = useConfig();
  const [code, setCode] = useState('');
  const [codeValidated, setCodeValidated] = useState(false);
  const [scoutName, setScoutName] = useState('');
  const [validating, setValidating] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Scouts list (loaded after code validation, so user can override)
  const [scouts, setScouts] = useState([]);
  const [selectedScoutId, setSelectedScoutId] = useState('');

  const [form, setForm] = useState({
    first_name: '', last_name: '', birth_date: '', document: '',
    passport: '', community_passport: '', nationality: '',
    phone: '', email: '', address: '', social_media: '',
    mother_name: '', mother_phone: '',
    father_name: '', father_phone: '',
    tutor_name: '', tutor_phone: '',
    medical_coverage: '', shoe_size: '',
    position: '', position_secondary: '',
    height: '', weight: '', preferred_foot: '', club_trajectory: '', origin_club: '',
  });

  // Valores custom cuando se elige "Otro/Otra" en un select
  const [customValues, setCustomValues] = useState({ nationality: '', medical_coverage: '', origin_club: '', position: '', shoe_size: '' });
  const handleCustomChange = (fieldName, text) => setCustomValues(prev => ({ ...prev, [fieldName]: text }));

  const handleValidateCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) { setCodeError('Ingresa el código'); return; }
    setValidating(true);
    setCodeError('');
    try {
      const res = await fetch(`${API}/public/validate-code/${code.trim()}`);
      if (res.ok) {
        const data = await res.json();
        setScoutName(data.scout_name);
        setCodeValidated(true);
        // Load scouts list so the user can confirm or change the scout
        try {
          const scoutsRes = await fetch(`${API}/public/scouts`);
          if (scoutsRes.ok) {
            const list = await scoutsRes.json();
            setScouts(list);
            // Pre-select the scout matching the code (best effort by name match)
            const match = list.find(s => s.name === data.scout_name);
            if (match) setSelectedScoutId(match.id);
          }
        } catch (_) { /* fail silent — fallback uses scout_code */ }
      } else {
        setCodeError('Código inválido. Verificá con tu representante.');
      }
    } catch { setCodeError('Error de conexión. Intentá de nuevo.'); }
    finally { setValidating(false); }
  };

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!form.first_name?.trim()) missing.push('Nombres');
    if (!form.last_name?.trim()) missing.push('Apellidos');
    if (!form.birth_date) missing.push('Fecha de Nacimiento');
    if (!form.document?.trim()) missing.push('Cédula de Identidad');
    if (!form.phone?.trim()) missing.push('Celular');
    if (!form.position?.trim()) missing.push('Posición');
    if (!form.preferred_foot?.trim()) missing.push('Pierna Hábil');
    const hasMother = form.mother_name?.trim() && form.mother_phone?.trim();
    const hasFather = form.father_name?.trim() && form.father_phone?.trim();
    const hasTutor  = form.tutor_name?.trim()  && form.tutor_phone?.trim();
    if (!hasMother && !hasFather && !hasTutor) {
      missing.push('Contacto familiar (Madre, Padre o Tutor: nombre + celular)');
    }
    if (missing.length > 0) {
      toast.error(`Faltan campos obligatorios: ${missing.join(', ')}`);
      return;
    }
    setSubmitting(true);
    try {
      // Resolver valores "Otro/Otra" con el texto custom
      const payload = {
        ...form,
        nationality: resolveOtherValue(form.nationality, customValues.nationality),
        medical_coverage: resolveOtherValue(form.medical_coverage, customValues.medical_coverage),
        origin_club: resolveOtherValue(form.origin_club, customValues.origin_club),
        position: resolveOtherValue(form.position, customValues.position),
        shoe_size: resolveOtherValue(form.shoe_size, customValues.shoe_size),
        code: code.trim(),
        scout_user_id: selectedScoutId || undefined,
      };

      // Persistir valores "Otra" al catálogo global para que aparezcan en próximos forms
      const isOther = (v) => v === 'Otra' || v === 'Otro';
      const catalogPromises = [];
      if (isOther(form.nationality) && customValues.nationality?.trim()) {
        catalogPromises.push(axios.post(`${API}/catalogs/nationalities`, { value: customValues.nationality.trim() }).catch(() => {}));
      }
      if (isOther(form.medical_coverage) && customValues.medical_coverage?.trim()) {
        catalogPromises.push(axios.post(`${API}/catalogs/medical_coverages`, { value: customValues.medical_coverage.trim() }).catch(() => {}));
      }
      if (isOther(form.origin_club) && customValues.origin_club?.trim()) {
        catalogPromises.push(axios.post(`${API}/catalogs/teams`, { value: customValues.origin_club.trim() }).catch(() => {}));
      }
      if (isOther(form.position) && customValues.position?.trim()) {
        catalogPromises.push(axios.post(`${API}/catalogs/positions`, { value: customValues.position.trim() }).catch(() => {}));
      }
      if (isOther(form.shoe_size) && customValues.shoe_size?.trim()) {
        catalogPromises.push(axios.post(`${API}/catalogs/shoe_sizes`, { value: customValues.shoe_size.trim() }).catch(() => {}));
      }
      await Promise.all(catalogPromises);

      const res = await fetch(`${API}/public/player-form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al enviar');
      }
    } catch { toast.error('Error de conexión'); }
    finally { setSubmitting(false); }
  };

  // ==================== CODE ENTRY SCREEN ====================
  if (!codeValidated) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]" data-testid="player-form-page">
        <Toaster position="top-right" />
        <Header />
        <div className="flex items-center justify-center px-4" style={{ minHeight: 'calc(100vh - 65px)' }}>
          <div className="w-full max-w-sm">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                <Lock className="w-7 h-7 text-white/40" />
              </div>
              <h1 className="text-2xl font-semibold text-white mb-2">Registro de Jugador</h1>
              <p className="text-white/40 text-sm font-light">Ingresá el código que te dio tu representante para acceder al formulario.</p>
            </div>
            <form onSubmit={handleValidateCode} className="space-y-4">
              <div>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => { setCode(e.target.value.toUpperCase()); setCodeError(''); }}
                  className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white text-center text-lg font-mono tracking-widest placeholder-white/20 focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-all duration-300 outline-none"
                  placeholder="CÓDIGO"
                  autoFocus
                  data-testid="code-input"
                />
                {codeError && (
                  <p className="text-red-400 text-sm text-center mt-2 font-light" data-testid="code-error">{codeError}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={validating}
                data-testid="code-submit-btn"
                className="w-full px-6 py-4 bg-white hover:bg-white/90 text-black font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {validating ? <Loader2 className="w-5 h-5 animate-spin" /> : <><span>Ingresar</span><ArrowRight className="w-5 h-5" /></>}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ==================== SUCCESS SCREEN ====================
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]" data-testid="player-form-page">
        <Header />
        <div className="flex items-center justify-center px-4" style={{ minHeight: 'calc(100vh - 65px)' }}>
          <div className="text-center max-w-md">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-white mb-2">Datos enviados</h1>
            <p className="text-white/50 font-light">Tu información fue recibida correctamente. El equipo de {config.AGENCY_NAME} se pondrá en contacto contigo.</p>
          </div>
        </div>
      </div>
    );
  }

  // ==================== FORM ====================
  const inputCls = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/20 focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-all duration-300 outline-none font-light text-sm";
  const labelCls = "block text-white/70 text-xs font-light mb-1.5 uppercase tracking-wider";

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="player-form-page">
      <Toaster position="top-right" />
      <Header />

      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl sm:text-3xl font-semibold text-white">Registro de Jugador</h1>
            <span className="text-white/30 text-xs font-light">Ref: {scoutName}</span>
          </div>
          <p className="text-white/50 text-sm font-light">Completá tus datos. Los campos marcados con * son obligatorios.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">

          {/* ── Captador / Scout ── */}
          {scouts.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-5">
                <User className="w-4 h-4 text-white/40" />
                <h2 className="text-white/60 text-xs font-light tracking-[0.2em] uppercase">Captador / Representante</h2>
              </div>
              <div>
                <label className={labelCls}>¿Quién te está captando? *</label>
                <select
                  value={selectedScoutId}
                  onChange={(e) => setSelectedScoutId(e.target.value)}
                  className={inputCls}
                  data-testid="form-scout-select"
                >
                  <option value="">Seleccionar...</option>
                  {scouts.map((s) => (
                    <option key={s.id} value={s.id} className="bg-gray-900">{s.name}</option>
                  ))}
                </select>
                <p className="text-white/40 text-xs mt-1 font-light">
                  Si no sabés quién, dejá el sugerido por el código.
                </p>
              </div>
            </section>
          )}

          {/* ── Datos Personales ── */}
          <section>
            <div className="flex items-center gap-2 mb-5">
              <User className="w-4 h-4 text-white/40" />
              <h2 className="text-white/60 text-xs font-light tracking-[0.2em] uppercase">Datos Personales</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Nombres *</label>
                <input name="first_name" value={form.first_name} onChange={handleChange} className={inputCls} placeholder="Ej: Juan Carlos" required data-testid="form-first-name" />
              </div>
              <div>
                <label className={labelCls}>Apellidos *</label>
                <input name="last_name" value={form.last_name} onChange={handleChange} className={inputCls} placeholder="Ej: Rodríguez Pérez" required data-testid="form-last-name" />
              </div>
              <div>
                <label className={labelCls}>Fecha de Nacimiento *</label>
                <input name="birth_date" type="date" value={form.birth_date} onChange={handleChange} className={inputCls} required data-testid="form-birth-date" />
              </div>
              <div>
                <label className={labelCls}>Nacionalidad</label>
                <SelectField name="nationality" value={form.nationality} onChange={handleChange} catalogType="nationalities" scoutCode={code.trim()} testId="form-nationality" otherValue={customValues.nationality} onOtherChange={handleCustomChange} />
              </div>
              <div>
                <label className={labelCls}>Cédula de Identidad *</label>
                <input name="document" value={form.document} onChange={handleChange} className={inputCls} placeholder="Ej: 5.123.456-7" required data-testid="form-document" />
              </div>
              <div>
                <label className={labelCls}>Pasaporte</label>
                <input name="passport" value={form.passport} onChange={handleChange} className={inputCls} placeholder="Número de pasaporte" data-testid="form-passport" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Pasaporte comunitario (europeo)</label>
                <SelectField name="community_passport" value={form.community_passport} onChange={handleChange} options={COMMUNITY_PASSPORT_OPTIONS} testId="form-community-passport" />
              </div>
            </div>
          </section>

          {/* ── Contacto ── */}
          <section>
            <div className="flex items-center gap-2 mb-5">
              <Phone className="w-4 h-4 text-white/40" />
              <h2 className="text-white/60 text-xs font-light tracking-[0.2em] uppercase">Contacto</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Celular *</label>
                <input name="phone" value={form.phone} onChange={handleChange} className={inputCls} placeholder="Ej: 099 123 456" required data-testid="form-phone" />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} className={inputCls} placeholder="Ej: juan@email.com" data-testid="form-email" />
              </div>
              <div>
                <label className={labelCls}>Redes Sociales</label>
                <input name="social_media" value={form.social_media} onChange={handleChange} className={inputCls} placeholder="@instagram, @tiktok, etc." data-testid="form-social-media" />
              </div>
              <div>
                <label className={labelCls}>Dirección de Domicilio</label>
                <input name="address" value={form.address} onChange={handleChange} className={inputCls} placeholder="Calle, número, ciudad" data-testid="form-address" />
              </div>
            </div>
          </section>

          {/* ── Familia / Tutor ── */}
          <section>
            <div className="flex items-center gap-2 mb-5">
              <FileText className="w-4 h-4 text-white/40" />
              <h2 className="text-white/60 text-xs font-light tracking-[0.2em] uppercase">Familia / Tutor</h2>
            </div>
            <p className="text-[11px] text-amber-300/80 mb-4 tracking-wide" data-testid="form-family-note">
              * Cargá al menos un contacto familiar (Madre, Padre o Tutor) con nombre y celular.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Nombre de la Madre</label>
                <input name="mother_name" value={form.mother_name} onChange={handleChange} className={inputCls} placeholder="Nombre completo" data-testid="form-mother-name" />
              </div>
              <div>
                <label className={labelCls}>Celular de la Madre</label>
                <input name="mother_phone" value={form.mother_phone} onChange={handleChange} className={inputCls} placeholder="Número" data-testid="form-mother-phone" />
              </div>
              <div>
                <label className={labelCls}>Nombre del Padre</label>
                <input name="father_name" value={form.father_name} onChange={handleChange} className={inputCls} placeholder="Nombre completo" data-testid="form-father-name" />
              </div>
              <div>
                <label className={labelCls}>Celular del Padre</label>
                <input name="father_phone" value={form.father_phone} onChange={handleChange} className={inputCls} placeholder="Número" data-testid="form-father-phone" />
              </div>
              <div>
                <label className={labelCls}>Nombre del Tutor (si aplica)</label>
                <input name="tutor_name" value={form.tutor_name} onChange={handleChange} className={inputCls} placeholder="Nombre completo" data-testid="form-tutor-name" />
              </div>
              <div>
                <label className={labelCls}>Celular del Tutor</label>
                <input name="tutor_phone" value={form.tutor_phone} onChange={handleChange} className={inputCls} placeholder="Número" data-testid="form-tutor-phone" />
              </div>
            </div>
          </section>

          {/* ── Datos Deportivos ── */}
          <section>
            <div className="flex items-center gap-2 mb-5">
              <Footprints className="w-4 h-4 text-white/40" />
              <h2 className="text-white/60 text-xs font-light tracking-[0.2em] uppercase">Datos Deportivos</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Posición Principal *</label>
                <SelectField name="position" value={form.position} onChange={handleChange} catalogType="positions" scoutCode={code.trim()} testId="form-position" otherValue={customValues.position} onOtherChange={handleCustomChange} />
              </div>
              <div>
                <label className={labelCls}>Posición Secundaria / Aclaración</label>
                <input name="position_secondary" value={form.position_secondary} onChange={handleChange} className={inputCls} placeholder="Ej: también juega de extremo" data-testid="form-position-secondary" />
              </div>
              <div>
                <label className={labelCls}>Pierna Hábil *</label>
                <SelectField name="preferred_foot" value={form.preferred_foot} onChange={handleChange} options={FOOT_OPTIONS} testId="form-preferred-foot" />
              </div>
              <div>
                <label className={labelCls}>Talle de Botines (EUR)</label>
                <SelectField name="shoe_size" value={form.shoe_size} onChange={handleChange} catalogType="shoe_sizes" scoutCode={code.trim()} testId="form-shoe-size" otherValue={customValues.shoe_size} onOtherChange={handleCustomChange} />
              </div>
              <div>
                <label className={labelCls}>Altura (metros)</label>
                <input name="height" value={form.height} onChange={handleChange} className={inputCls} placeholder="Ej: 1.78" data-testid="form-height" />
              </div>
              <div>
                <label className={labelCls}>Peso (kg)</label>
                <input name="weight" value={form.weight} onChange={handleChange} className={inputCls} placeholder="Ej: 70" data-testid="form-weight" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Mutualista / Sociedad Médica</label>
                <SelectField name="medical_coverage" value={form.medical_coverage} onChange={handleChange} catalogType="medical_coverages" scoutCode={code.trim()} testId="form-medical-coverage" otherValue={customValues.medical_coverage} onOtherChange={handleCustomChange} />
              </div>
            </div>
          </section>

          {/* ── Trayectoria ── */}
          <section>
            <div className="flex items-center gap-2 mb-5">
              <ClipboardList className="w-4 h-4 text-white/40" />
              <h2 className="text-white/60 text-xs font-light tracking-[0.2em] uppercase">Trayectoria</h2>
            </div>
            <div>
              <label className={labelCls}>Equipo actual</label>
              <SelectField name="origin_club" value={form.origin_club} onChange={handleChange} catalogType="teams" scoutCode={code.trim()} testId="form-origin-club" otherValue={customValues.origin_club} onOtherChange={handleCustomChange} />
            </div>
            <div>
              <label className={labelCls}>Clubes donde jugaste antes (opcional)</label>
              <textarea
                name="club_trajectory"
                value={form.club_trajectory}
                onChange={handleChange}
                rows={3}
                className={inputCls + " resize-none"}
                placeholder="Ej: Nacional (2020-2022), Fénix (2022-actual)"
                data-testid="form-club-trajectory"
              />
            </div>
          </section>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            data-testid="form-submit-btn"
            className="w-full px-6 py-4 bg-white hover:bg-white/90 text-black font-medium rounded-xl transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50"
          >
            {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Enviando...</> : 'Enviar datos'}
          </button>
        </form>

        <p className="text-white/20 text-xs text-center mt-8 font-light">
          {config.AGENCY_NAME} — {config.AGENCY_DESCRIPTION}
        </p>
      </div>
    </div>
  );
}
